/* eslint-disable max-len */
import {ReloadOutlined} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  type InputRef,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  type FormInstance
} from 'antd';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import AlertDescription from '../../../components/AlertDescription/AlertDescription';
import SelectWithOtherOption from '../../../components/CustomInputs/Select/SelectWithOtherOption';
import {excludeSacProducts} from '../../../components/Forms/Constants/Products/ProductInfoKeyLabel';
import {filterSelectOptions} from '../../../components/Forms/Utils/commonFunctions';
import Loader from '../../../components/Loader/Loader';
import PageContent from '../../../components/PageContent/PageContent';
import MainTableTitle from '../../../components/Table/Utils/MainTableTitle';
import {EfdbName} from '../../../constants/efdbNameConstants';
import OPCategory from '../../../services/modules/JSON/categoryOP';
import productsJSON from '../../../services/modules/JSON/products';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import usePCFStore from '../../../store/pcfStore/pcfStore';
import useJSONStore from '../../../store/StaticJson/jsonStore';
import showAlertNotification from '../../../utils/showAlertNotification';
import {CN_CODE_OPTIONS} from '../../esgManager/constants/cbam/IronandSteel/ironAndSteelOptions';
import {GOAL_SCOPE_FIELD_HINTS} from '../constants/goalScopeFieldHints';
import {isGoalScopeFieldMandatory} from '../constants/goalScopeFieldMeta';
import {
  getGoalScopeInitialValues,
  GOAL_SCOPE_INITIAL_VALUES
} from '../constants/pcfDefaultValues';
import {rowGutter} from '../constants/pcfFormLayout';
import {
  INTENDED_APPLICATIONS,
  PUBLIC_FACING_APPLICATION_VALUES,
  CBAM_APPLICATION_VALUE,
  PCF_STANDARDS,
  INTENDED_AUDIENCES,
  FU_UNITS,
  SYSTEM_BOUNDARY_SCOPES,
  GEOGRAPHIC_SCOPES,
  ALLOCATION_METHODS,
  LIFE_CYCLE_STAGES,
  STAGES_BY_BOUNDARY
} from '../constants/pcfOptions';
import {
  PCF_VERIFICATION_WARNING,
  PCF_VERIFICATION_WARNING_KEY
} from '../constants/pcfVerificationWarning';
import usePCFPersist from '../hooks/usePCFPersist';
import {usePCFStepHydration} from '../hooks/usePCFStepHydration';
import {GoalAndScopeKeys, GoalAndScopeLabels} from '../PCFKeyLabels';

import {FormItemLabel} from './FormItemLabel';
import {
  allocationMethodRules,
  audienceOtherRules,
  audienceSelectionRules,
  boundaryScopeRules,
  checkDuplicateStudyName,
  exclusionsRules,
  fuDescriptionRules,
  fuQuantityRules,
  fuUnitOtherRules,
  fuUnitRules,
  geographicScopeRules,
  hsnCodeRules,
  industrySectorRules,
  intendedApplicationRules,
  pcfStandardRules,
  productCNCodeRules,
  productCodeRules,
  productNameRules,
  referenceFlowRules,
  referenceYearRules,
  reportingOrgRules,
  studyDateRules,
  studyNameRules
} from './goalScopeValidation';
import PCFFooterPrimaryButton from './PCFFooterPrimaryButton';
import {runPCFDraftSave} from './pcfFormDraft';
import PCFFormHeader from './PCFFormHeader';
import {
  isReferenceYearAllowedForStudyDate,
  normalizeReferenceYearValue
} from './referenceYearUtils';
import StudyDateInput from './StudyDateInput';
import StudyReferenceYearInput from './StudyReferenceYearInput';
import TabStepper from './TabStepper';

type AudienceKey =
  | 'internal'
  | 'management'
  | 'customers'
  | 'regulatory'
  | 'epd'
  | 'public'
  | 'financial'
  | 'esg_investor'
  | 'other';

type StageId =
  | 'A1'
  | 'A2'
  | 'A3'
  | 'A4'
  | 'A5'
  | 'B1'
  | 'B2'
  | 'B3'
  | 'B4'
  | 'B5'
  | 'B6'
  | 'B7'
  | 'C1'
  | 'C2'
  | 'C3'
  | 'C4'
  | 'D';

const goalScopeValidateTrigger: Array<'onChange' | 'onBlur'> = [
  'onChange',
  'onBlur'
];

function mergeLockedStages(form: FormInstance, boundaryScope: string) {
  const locked = STAGES_BY_BOUNDARY[boundaryScope] || [];
  const currentStages =
    (form.getFieldValue(GoalAndScopeKeys.STAGES) as Partial<
      Record<StageId, boolean>
    >) || {};
  const next = {...currentStages};
  locked.forEach((s) => {
    next[s] = true;
  });
  form.setFieldsValue({[GoalAndScopeKeys.STAGES]: next});
}

const GoalAndScopePage: React.FC = () => {
  const [form] = Form.useForm();
  const [submitFailed, setSubmitFailed] = useState(false);
  const [dropdownsReady, setDropdownsReady] = useState(false);
  const [duplicateStudyNameAcknowledged, setDuplicateStudyNameAcknowledged] =
    useState(false);
  const studyNameInputRef = useRef<InputRef | null>(null);

  const {
    recordId,
    isStepReady,
    navigateToStep,
    reloadFromServer
  } = usePCFStepHydration(form, 'goalAndScope');
  const {isSaving, saveDraftLabel, actionLabel, persist, runWhileSaving} =
    usePCFPersist();
  const recordData = usePCFStore((state) => state.recordData);

  const companyId = useCompanyStore((state) => state.companyId);
  const companyDetails = useCompanyStore((state) => state.companyDetails);
  const categories = useJSONStore((state) => state.category);
  const allProducts = useJSONStore((state) => state.products);

  useEffect(() => {
    Promise.all([OPCategory(), productsJSON()])
      .catch(() => {})
      .finally(() => {
        setDropdownsReady(true);
      });
  }, []);

  useEffect(() => {
    const companyName = companyDetails?.companyName;
    const currentReportingOrg = form.getFieldValue(
      GoalAndScopeKeys.REPORTING_ORG
    );

    if (
      companyName &&
      (currentReportingOrg == null || String(currentReportingOrg).trim() === '')
    ) {
      form.setFieldValue(GoalAndScopeKeys.REPORTING_ORG, companyName);
    }
  }, [companyDetails?.companyName, form]);

  const goalScopeInitialValues = useMemo(() => {
    const persisted = recordData.goalAndScope;

    if (persisted != null && Object.keys(persisted).length > 0) {
      return {
        ...persisted,
        referenceYear: normalizeReferenceYearValue(persisted.referenceYear)
      };
    }

    return getGoalScopeInitialValues();
  }, [companyDetails?.companyName, recordData.goalAndScope]);

  const hsnProductOptions = useMemo(
    () =>
      excludeSacProducts(
        allProducts.filter((item) => item.dbName === EfdbName.eeio)
      ),
    [allProducts]
  );

  const boundaryScope =
    Form.useWatch(GoalAndScopeKeys.BOUNDARY_SCOPE, form) ??
    GOAL_SCOPE_INITIAL_VALUES.boundaryScope;
  const intendedApplication =
    Form.useWatch(GoalAndScopeKeys.INTENDED_APPLICATION, form) ?? '';
  const audienceSelection =
    (Form.useWatch(GoalAndScopeKeys.INTENDED_AUDIENCES, form) as
      | AudienceKey[]
      | undefined) ?? GOAL_SCOPE_INITIAL_VALUES.audienceSelection;
  const fuUnit =
    Form.useWatch(GoalAndScopeKeys.FU_UNIT, form) ??
    GOAL_SCOPE_INITIAL_VALUES.fuUnit;
  const studyDate = Form.useWatch(GoalAndScopeKeys.STUDY_DATE, form);

  const lockedStages = useMemo(
    () => STAGES_BY_BOUNDARY[boundaryScope] || [],
    [boundaryScope]
  );

  const cnCodeRequired = intendedApplication === CBAM_APPLICATION_VALUE;

  const showVerificationWarning =
    PUBLIC_FACING_APPLICATION_VALUES.includes(intendedApplication) ||
    audienceSelection.includes('public') ||
    audienceSelection.includes('regulatory');

  const prevVerificationWarningRef = useRef(false);

  useEffect(() => {
    if (!dropdownsReady) {
      return;
    }

    const shouldNotify =
      showVerificationWarning && !prevVerificationWarningRef.current;

    prevVerificationWarningRef.current = showVerificationWarning;

    if (!shouldNotify) {
      return;
    }

    showAlertNotification({
      type: 'warning',
      key: PCF_VERIFICATION_WARNING_KEY,
      ...PCF_VERIFICATION_WARNING
    });
  }, [dropdownsReady, showVerificationWarning]);

  const stageGateDependencies = useMemo(
    () => [GoalAndScopeKeys.BOUNDARY_SCOPE, GoalAndScopeKeys.STAGES] as const,
    []
  );

  const persistGoalScope = async (
    status: 'draft' | 'approved'
  ): Promise<void> => {
    await persist({
      section: 'goalAndScope',
      sectionValues: form.getFieldsValue(true),
      status
    });
  };

  const proceedToNextStep = (): void => {
    setSubmitFailed(false);
    navigateToStep('lciDataCollection');
  };

  const handleSaveDraft = (): void => {
    runWhileSaving(async () => {
      setSubmitFailed(false);
      await runPCFDraftSave(form, async () => {
        await persistGoalScope('draft');
      });
    }).catch(() => {});
  };

  const handleSaveContinue = (): void => {
    runWhileSaving(async () => {
      await form.validateFields();

      const studyName = String(
        form.getFieldValue(GoalAndScopeKeys.STUDY_NAME) ?? ''
      );

      if (!duplicateStudyNameAcknowledged) {
        const {isDuplicate, message} = await checkDuplicateStudyName(
          studyName,
          companyId,
          recordId ?? undefined
        );

        if (isDuplicate) {
          Modal.confirm({
            title: 'Duplicate study name',
            content:
              message ??
              'A study with this name already exists for your organisation. You can rename it or proceed anyway.',
            okText: 'Rename',
            cancelText: 'Proceed anyway',
            onOk: () => {
              studyNameInputRef.current?.focus();
            },
            onCancel: () => {
              setDuplicateStudyNameAcknowledged(true);
              runWhileSaving(async () => {
                await persistGoalScope('approved');
                proceedToNextStep();
              }).catch(() => {});
            }
          });

          return;
        }
      }

      await persistGoalScope('approved');
      proceedToNextStep();
    }).catch(() => {
      setSubmitFailed(true);
      setTimeout(() => {
        document
          .querySelector(
            '.stages-block .goal-scope-stage-gate-item.ant-form-item-has-error, .ant-form-item-has-error'
          )
          ?.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 50);
    });
  };

  const showFormErrorSummary =
    submitFailed && form.getFieldsError().some(({errors}) => errors.length > 0);

  const pageShell = (
    <Flex
      className="goal-and-scope"
      vertical
      justify="space-between"
      align="center"
      gap={24}
      style={{height: '100%'}}
    >
      <Flex
        vertical={false}
        justify="space-between"
        align="center"
        style={{width: '100%'}}
      >
        <MainTableTitle
          title="Product Carbon Footprint (PCF)"
          showDivider={false}
        />
        <Flex vertical={false} gap={10} align="center" justify="center">
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={() => {
              reloadFromServer().catch(() => {});
            }}
            disabled={isSaving}
          />
          <Button
            type="primary"
            htmlType="button"
            onClick={handleSaveDraft}
            loading={isSaving}
            disabled={isSaving}
          >
            {saveDraftLabel}
          </Button>
        </Flex>
      </Flex>
      <TabStepper activeStep={1} completedSteps={[]} recordId={recordId} />
      <PageContent>
        <div className="page-content">
          {!isStepReady || !dropdownsReady ? (
            <Loader topMargin />
          ) : (
            <>
              <PCFFormHeader
                title="1. Goal & Scope Definition"
                description="Define study metadata, functional unit, standard, and system boundary."
              />

              {showVerificationWarning && (
                <AlertDescription
                  type="warning"
                  message={PCF_VERIFICATION_WARNING.message}
                  description={PCF_VERIFICATION_WARNING.description}
                  style={{marginBottom: 20}}
                />
              )}

              <Form
                form={form}
                layout="vertical"
                initialValues={goalScopeInitialValues}
                validateTrigger={goalScopeValidateTrigger}
                scrollToFirstError={{behavior: 'smooth', block: 'center'}}
                className="goal-scope-form"
                onValuesChange={(changedValues) => {
                  if (submitFailed) setSubmitFailed(false);
                  if (
                    Object.prototype.hasOwnProperty.call(
                      changedValues,
                      GoalAndScopeKeys.STUDY_NAME
                    )
                  ) {
                    setDuplicateStudyNameAcknowledged(false);
                  }
                  if (
                    Object.prototype.hasOwnProperty.call(
                      changedValues,
                      GoalAndScopeKeys.INTENDED_APPLICATION
                    ) &&
                    changedValues[GoalAndScopeKeys.INTENDED_APPLICATION] !==
                      CBAM_APPLICATION_VALUE
                  ) {
                    form.setFieldsValue({
                      [GoalAndScopeKeys.PRODUCT_CN_CODE]: undefined
                    });
                  }
                  if (
                    Object.prototype.hasOwnProperty.call(
                      changedValues,
                      GoalAndScopeKeys.INTENDED_AUDIENCES
                    )
                  ) {
                    const nextAudience = changedValues[
                      GoalAndScopeKeys.INTENDED_AUDIENCES
                    ] as AudienceKey[] | undefined;
                    if (!nextAudience?.includes('other')) {
                      form.setFieldsValue({
                        [GoalAndScopeKeys.AUDIENCE_OTHER]: undefined
                      });
                    }
                  }
                  if (
                    Object.prototype.hasOwnProperty.call(
                      changedValues,
                      GoalAndScopeKeys.STUDY_DATE
                    )
                  ) {
                    const nextStudyDate = changedValues[
                      GoalAndScopeKeys.STUDY_DATE
                    ] as string | undefined;
                    const currentReference = form.getFieldValue(
                      GoalAndScopeKeys.REFERENCE_YEAR
                    );

                    if (
                      currentReference &&
                      nextStudyDate &&
                      !isReferenceYearAllowedForStudyDate(
                        currentReference,
                        nextStudyDate
                      )
                    ) {
                      form.setFieldsValue({
                        [GoalAndScopeKeys.REFERENCE_YEAR]: undefined
                      });
                    }
                  }
                  if (
                    Object.prototype.hasOwnProperty.call(
                      changedValues,
                      GoalAndScopeKeys.FU_UNIT
                    ) &&
                    changedValues[GoalAndScopeKeys.FU_UNIT] !== 'other_specify'
                  ) {
                    form.setFieldsValue({
                      [GoalAndScopeKeys.FU_UNIT_OTHER]: undefined
                    });
                  }
                }}
              >
                {/* CARD 1 */}
                <section className="card">
                  <div className="card-body">
                    <Row gutter={rowGutter}>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.STUDY_NAME}
                          required={isGoalScopeFieldMandatory('studyName')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.studyName}
                            >
                              {GoalAndScopeLabels.STUDY_NAME}
                            </FormItemLabel>
                          }
                          rules={studyNameRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input
                            ref={studyNameInputRef}
                            placeholder='e.g., "Polypropylene Bracket FY2026"'
                            maxLength={100}
                            className="goal-scope-control"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.PRODUCT_NAME}
                          required={isGoalScopeFieldMandatory('productName')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.productName}
                            >
                              {GoalAndScopeLabels.PRODUCT_NAME}
                            </FormItemLabel>
                          }
                          rules={productNameRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input
                            placeholder='e.g., "Polypropylene Bracket"'
                            maxLength={150}
                            className="goal-scope-control"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.STUDY_DESCRIPTION}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.studyDescription}
                            >
                              {GoalAndScopeLabels.STUDY_DESCRIPTION}
                            </FormItemLabel>
                          }
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input.TextArea
                            placeholder='e.g., "Prototype study for design and development alignment."'
                            maxLength={500}
                            rows={4}
                            className="goal-scope-control goal-scope-textarea"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.PRODUCT_CODE}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.productCode}
                            >
                              {GoalAndScopeLabels.PRODUCT_CODE}
                            </FormItemLabel>
                          }
                          rules={productCodeRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input
                            placeholder='e.g., "PPB1001"'
                            maxLength={50}
                            className="goal-scope-control"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.INDUSTRY_SECTOR}
                          required={isGoalScopeFieldMandatory('industrySector')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.industrySector}
                            >
                              {GoalAndScopeLabels.INDUSTRY_SECTOR}
                            </FormItemLabel>
                          }
                          rules={industrySectorRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <SelectWithOtherOption
                            mode="multiple"
                            showSearch
                            allowClear
                            maxLength={80}
                            placeholder="Select"
                            options={categories}
                            filterOption={filterSelectOptions}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.HS_CODE}
                          required={isGoalScopeFieldMandatory('hsnCode')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.hsnCode}
                            >
                              {GoalAndScopeLabels.HS_CODE}
                            </FormItemLabel>
                          }
                          rules={hsnCodeRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            mode="multiple"
                            showSearch
                            allowClear
                            placeholder="Select HSN code(s)"
                            options={hsnProductOptions}
                            filterOption={filterSelectOptions}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.REPORTING_ORG}
                          required={isGoalScopeFieldMandatory('reportingOrg')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.reportingOrg}
                            >
                              {GoalAndScopeLabels.REPORTING_ORG}
                            </FormItemLabel>
                          }
                          rules={reportingOrgRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input
                            maxLength={200}
                            className="goal-scope-control"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.STUDY_DATE}
                          required={isGoalScopeFieldMandatory('studyDate')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.studyDate}
                            >
                              {GoalAndScopeLabels.STUDY_DATE}
                            </FormItemLabel>
                          }
                          rules={studyDateRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <StudyDateInput className="goal-scope-control" />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.REFERENCE_YEAR}
                          required={isGoalScopeFieldMandatory('referenceYear')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.referenceYear}
                            >
                              {GoalAndScopeLabels.REFERENCE_YEAR}
                            </FormItemLabel>
                          }
                          normalize={normalizeReferenceYearValue}
                          rules={referenceYearRules(() =>
                            form.getFieldValue(GoalAndScopeKeys.STUDY_DATE)
                          )}
                          validateFirst
                          hasFeedback
                          validateTrigger={[]}
                        >
                          <StudyReferenceYearInput
                            className="goal-scope-control"
                            studyDate={studyDate}
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.INTENDED_APPLICATION}
                          required={isGoalScopeFieldMandatory(
                            'intendedApplication'
                          )}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.intendedApplication}
                            >
                              {GoalAndScopeLabels.INTENDED_APPLICATION}
                            </FormItemLabel>
                          }
                          rules={intendedApplicationRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            placeholder="Select"
                            options={INTENDED_APPLICATIONS}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.PRODUCT_CN_CODE}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.productCNCode}
                            >
                              {GoalAndScopeLabels.PRODUCT_CN_CODE}
                            </FormItemLabel>
                          }
                          required={cnCodeRequired}
                          dependencies={[GoalAndScopeKeys.INTENDED_APPLICATION]}
                          rules={productCNCodeRules(() =>
                            form.getFieldValue(
                              GoalAndScopeKeys.INTENDED_APPLICATION
                            )
                          )}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            showSearch
                            allowClear
                            disabled={!cnCodeRequired}
                            placeholder={
                              cnCodeRequired
                                ? 'Select CN code'
                                : 'Select Regulatory compliance to enable'
                            }
                            options={CN_CODE_OPTIONS}
                            filterOption={filterSelectOptions}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.PCF_STANDARD}
                          required={isGoalScopeFieldMandatory('pcfStandard')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.pcfStandard}
                            >
                              {GoalAndScopeLabels.PCF_STANDARD}
                            </FormItemLabel>
                          }
                          rules={pcfStandardRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            placeholder="Select"
                            options={PCF_STANDARDS}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div className="audience-block">
                      <Form.Item
                        name={GoalAndScopeKeys.INTENDED_AUDIENCES}
                        required={isGoalScopeFieldMandatory(
                          'audienceSelection'
                        )}
                        label={
                          <FormItemLabel
                            hint={GOAL_SCOPE_FIELD_HINTS.intendedAudiences}
                          >
                            {GoalAndScopeLabels.INTENDED_AUDIENCES}
                          </FormItemLabel>
                        }
                        rules={audienceSelectionRules}
                        validateFirst
                        hasFeedback
                        validateTrigger={goalScopeValidateTrigger}
                      >
                        <Checkbox.Group className="audience-checkbox-group">
                          {INTENDED_AUDIENCES.map((aud) => (
                            <Checkbox key={aud.value} value={aud.value}>
                              {aud.label}
                            </Checkbox>
                          ))}
                        </Checkbox.Group>
                      </Form.Item>

                      {audienceSelection.includes('other') && (
                        <div className="audience-other">
                          <Form.Item
                            name={GoalAndScopeKeys.AUDIENCE_OTHER}
                            label={
                              <FormItemLabel withInfo={false}>
                                {GoalAndScopeLabels.AUDIENCE_OTHER}
                              </FormItemLabel>
                            }
                            required
                            dependencies={[GoalAndScopeKeys.INTENDED_AUDIENCES]}
                            rules={audienceOtherRules(
                              () =>
                                form.getFieldValue(
                                  GoalAndScopeKeys.INTENDED_AUDIENCES
                                ) as string[] | undefined
                            )}
                            validateFirst
                            hasFeedback
                            validateTrigger={goalScopeValidateTrigger}
                          >
                            <Input
                              placeholder="Specify other audience"
                              maxLength={80}
                              className="goal-scope-control"
                            />
                          </Form.Item>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* CARD 2 */}
                <section className="card">
                  <div className="card-body">
                    <h3 className="section-title">
                      Functional Unit Definition
                    </h3>

                    <Row gutter={rowGutter}>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.FU_QUANTITY}
                          required={isGoalScopeFieldMandatory('fuQuantity')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.fuQuantity}
                            >
                              {GoalAndScopeLabels.FU_QUANTITY}
                            </FormItemLabel>
                          }
                          rules={fuQuantityRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <InputNumber
                            min={0.0001}
                            precision={4}
                            step={0.0001}
                            style={{width: '100%'}}
                            className="goal-scope-input-number"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.FU_UNIT}
                          required={isGoalScopeFieldMandatory('fuUnit')}
                          label={
                            <FormItemLabel hint={GOAL_SCOPE_FIELD_HINTS.fuUnit}>
                              {GoalAndScopeLabels.FU_UNIT}
                            </FormItemLabel>
                          }
                          rules={fuUnitRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            placeholder="Select"
                            options={FU_UNITS}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                      {fuUnit === 'other_specify' && (
                        <Col className="gutter-row" span={24}>
                          <Form.Item
                            name={GoalAndScopeKeys.FU_UNIT_OTHER}
                            label={
                              <FormItemLabel withInfo={false}>
                                {GoalAndScopeLabels.FU_UNIT_OTHER}
                              </FormItemLabel>
                            }
                            required
                            dependencies={[GoalAndScopeKeys.FU_UNIT]}
                            rules={fuUnitOtherRules(() =>
                              form.getFieldValue(GoalAndScopeKeys.FU_UNIT)
                            )}
                            validateFirst
                            hasFeedback
                            validateTrigger={goalScopeValidateTrigger}
                          >
                            <SelectWithOtherOption
                              showSearch
                              allowClear
                              maxLength={20}
                              placeholder="Select"
                              options={[]}
                              filterOption={filterSelectOptions}
                              className="goal-scope-select"
                              popupClassName="goal-scope-select-dropdown"
                            />
                          </Form.Item>
                        </Col>
                      )}
                      <Col className="gutter-row" span={24}>
                        <Form.Item
                          name={GoalAndScopeKeys.FU_DESCRIPTION}
                          required={isGoalScopeFieldMandatory('fuDescription')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.fuDescription}
                            >
                              {GoalAndScopeLabels.FU_DESCRIPTION}
                            </FormItemLabel>
                          }
                          rules={fuDescriptionRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input.TextArea
                            rows={3}
                            maxLength={300}
                            className="goal-scope-control goal-scope-textarea"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={24}>
                        <Form.Item
                          name={GoalAndScopeKeys.REFERENCE_FLOW}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.referenceFlow}
                            >
                              {GoalAndScopeLabels.REFERENCE_FLOW}
                            </FormItemLabel>
                          }
                          rules={referenceFlowRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input
                            maxLength={200}
                            className="goal-scope-control"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </section>

                {/* CARD 3 */}
                <section className="card">
                  <div className="card-body">
                    <h3 className="section-title">System Boundary</h3>

                    <Row gutter={rowGutter}>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.BOUNDARY_SCOPE}
                          required={isGoalScopeFieldMandatory('boundaryScope')}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.boundaryScope}
                            >
                              {GoalAndScopeLabels.BOUNDARY_SCOPE}
                            </FormItemLabel>
                          }
                          rules={boundaryScopeRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            placeholder="Select"
                            options={SYSTEM_BOUNDARY_SCOPES}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                            onChange={(val: string) => {
                              mergeLockedStages(form, val);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={12}>
                        <Form.Item
                          name={GoalAndScopeKeys.GEOGRAPHIC_SCOPE}
                          required={isGoalScopeFieldMandatory(
                            'geographicScope'
                          )}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.geographicScope}
                            >
                              {GoalAndScopeLabels.GEOGRAPHIC_SCOPE}
                            </FormItemLabel>
                          }
                          rules={geographicScopeRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            placeholder="Select"
                            options={GEOGRAPHIC_SCOPES}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <div className="stages-block">
                      <Form.Item
                        label={
                          <FormItemLabel
                            hint={GOAL_SCOPE_FIELD_HINTS.lifeCycleStages}
                          >
                            {GoalAndScopeLabels.LIFE_CYCLE_STAGES}
                          </FormItemLabel>
                        }
                        required
                        validateTrigger={goalScopeValidateTrigger}
                      >
                        <div className="stages-grid">
                          {LIFE_CYCLE_STAGES.map((s) => {
                            const isLocked = lockedStages.includes(s.value);

                            return (
                              <div
                                key={s.value}
                                className={`stage-row ${
                                  isLocked ? 'locked' : ''
                                }`}
                                title={
                                  isLocked
                                    ? 'Required for this system boundary'
                                    : ''
                                }
                              >
                                <div className="checkbox-row">
                                  <Form.Item
                                    name={[GoalAndScopeKeys.STAGES, s.value]}
                                    valuePropName="checked"
                                    validateTrigger={goalScopeValidateTrigger}
                                    noStyle
                                  >
                                    <Checkbox disabled={isLocked}>
                                      {s.label}
                                    </Checkbox>
                                  </Form.Item>
                                </div>
                                {isLocked && (
                                  <span className="stage-locked-badge">
                                    <svg
                                      width="11"
                                      height="11"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <rect
                                        x="5"
                                        y="11"
                                        width="14"
                                        height="10"
                                        rx="1.5"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                      />
                                      <path
                                        d="M8 11V7a4 4 0 018 0v4"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                    Required
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Form.Item>

                      <Form.Item
                        name={GoalAndScopeKeys.STAGE_GATE}
                        className="goal-scope-stage-gate-item"
                        dependencies={[...stageGateDependencies]}
                        validateTrigger={goalScopeValidateTrigger}
                        rules={[
                          {
                            validator: async (_, __) => {
                              const bs = form.getFieldValue(
                                GoalAndScopeKeys.BOUNDARY_SCOPE
                              );
                              const stages =
                                (form.getFieldValue(
                                  GoalAndScopeKeys.STAGES
                                ) as Partial<Record<StageId, boolean>>) || {};
                              const locked = STAGES_BY_BOUNDARY[bs] || [];
                              if (!locked.every((id) => stages[id])) {
                                throw new Error(
                                  'Required stages must remain selected'
                                );
                              }
                              await Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          readOnly
                          tabIndex={-1}
                          aria-hidden
                          className="goal-scope-stage-gate-input"
                        />
                      </Form.Item>
                    </div>

                    <Row gutter={rowGutter}>
                      <Col className="gutter-row" span={24}>
                        <Form.Item
                          name={GoalAndScopeKeys.EXCLUSIONS}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.exclusions}
                            >
                              {GoalAndScopeLabels.EXCLUSIONS}
                            </FormItemLabel>
                          }
                          rules={exclusionsRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Input.TextArea
                            maxLength={500}
                            rows={3}
                            className="goal-scope-control goal-scope-textarea"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={24}>
                        <Form.Item
                          name={GoalAndScopeKeys.ALLOCATION_METHOD}
                          required={isGoalScopeFieldMandatory(
                            'allocationMethod'
                          )}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.allocationMethod}
                            >
                              {GoalAndScopeLabels.ALLOCATION_METHOD}
                            </FormItemLabel>
                          }
                          rules={allocationMethodRules}
                          validateFirst
                          hasFeedback
                          validateTrigger={goalScopeValidateTrigger}
                        >
                          <Select
                            placeholder="Select"
                            options={ALLOCATION_METHODS}
                            className="goal-scope-select"
                            popupClassName="goal-scope-select-dropdown"
                          />
                        </Form.Item>
                      </Col>
                      <Col className="gutter-row" span={24}>
                        <Form.Item
                          name={GoalAndScopeKeys.EXPERT_MODE}
                          label={
                            <FormItemLabel
                              hint={GOAL_SCOPE_FIELD_HINTS.expertMode}
                            >
                              {GoalAndScopeLabels.EXPERT_MODE}
                            </FormItemLabel>
                          }
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </section>
              </Form>

              {showFormErrorSummary && (
                <div className="form-error-summary">
                  Please complete all mandatory fields marked with * before
                  continuing.
                </div>
              )}

              <div className="footer-actions footer-actions-right">
                <PCFFooterPrimaryButton
                  onClick={handleSaveContinue}
                  label={actionLabel('Save & Continue')}
                  isSaving={isSaving}
                />
              </div>
            </>
          )}
        </div>
      </PageContent>
    </Flex>
  );

  return pageShell;
};

export default GoalAndScopePage;
